import { Check, ChevronDown, FileText, LucideIcon } from "lucide-react";
import { useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/src/components/shadcn/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/src/components/shadcn/ui/collapsible";

interface FieldsetHeaderProps {
  title: string;
  children: React.ReactNode;

  // State management (can be controlled or uncontrolled)
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  defaultOpen?: boolean;

  // Optional status indicator to show in the header
  status?: React.ReactNode;

  // For automatic conflict indicator. null indicates that conflicts don't apply.
  conflictCount?: number | null;

  // Optional icon to show in the header
  icon?: LucideIcon;
}

export default function FieldsetHeader({
  title,
  children,
  isOpen: controlledIsOpen,
  onOpenChange,
  defaultOpen = true,
  status,
  icon: Icon = FileText,
  conflictCount = null,
}: FieldsetHeaderProps) {
  // Use internal state for uncontrolled mode, external state for controlled mode
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(defaultOpen);
  const hasConflicts = conflictCount !== null && conflictCount > 0;

  // Determine if we're in controlled or uncontrolled mode
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen;

  // Handle state changes
  const handleOpenChange = (open: boolean) => {
    if (!isControlled) {
      setUncontrolledIsOpen(open);
    }

    if (onOpenChange) {
      onOpenChange(open);
    }
  };

  // Generate automatic status indicator based on conflict data
  const autoStatus = hasConflicts !== undefined && (
    <>
      {hasConflicts && (
        <div className="mr-2 px-2 py-0.5 text-xs bg-warn text-warn-foreground rounded-full flex items-center">
          {conflictCount} {conflictCount === 1 ? "Conflict" : "Conflicts"}
        </div>
      )}
      {conflictCount === 0 && !isOpen && (
        <div className="mr-2 px-1 py-0 text-xs bg-primary text-white dark:text-black rounded-full flex items-center justify-center w-4 h-4">
          <Check className="stroke-[2.5]" />
        </div>
      )}
    </>
  );

  // Use custom status if provided, otherwise use automatic status
  const displayStatus = status || autoStatus;

  // Collapsible header
  return (
    <Card className="space-y-2 bg-card mx-0 px-0 rounded-none">
      <Collapsible open={isOpen} onOpenChange={handleOpenChange} className="w-full">
        <CardHeader className="p-0 text-sm bg-muted hover:bg-accent space-y-0">
          <CollapsibleTrigger className="p-2 flex justify-between items-center w-full">
            <div className="flex items-center min-w-0 flex-1">
              <Icon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
              <CardTitle className="truncate">{title}</CardTitle>
            </div>
            <div className="flex items-center">
              {displayStatus}
              <ChevronDown className={`ml-2 h-4 w-4 shrink-0 opacity-50`} />
            </div>
          </CollapsibleTrigger>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="p-2">{children}</CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
